import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import clinicService from '../../../../services/clinic.service';

const MEDIUM_META = {
    EMAIL: { icon: 'bi-envelope-fill', className: 'activity-medium--email', label: 'Email' },
    SMS: { icon: 'bi-chat-text-fill', className: 'activity-medium--sms', label: 'SMS' },
    CALL: { icon: 'bi-telephone-fill', className: 'activity-medium--call', label: 'Call' },
};

const STATUS_META = {
    SENT: { className: 'activity-status--sent', icon: 'bi-check-circle-fill', label: 'Sent' },
    DELIVERED: { className: 'activity-status--delivered', icon: 'bi-check2-all', label: 'Delivered' },
    OPENED: { className: 'activity-status--opened', icon: 'bi-envelope-open-fill', label: 'Read' },
    FAILED: { className: 'activity-status--failed', icon: 'bi-x-circle-fill', label: 'Failed' },
    ERROR: { className: 'activity-status--error', icon: 'bi-exclamation-circle-fill', label: 'Error' },
    BOUNCED: { className: 'activity-status--bounced', icon: 'bi-exclamation-octagon-fill', label: 'Bounced' },
    QUEUED: { className: 'activity-status--queued', icon: 'bi-hourglass-split', label: 'Queued' },
    WAITING: { className: 'activity-status--waiting', icon: 'bi-clock-history', label: 'Waiting' },
    RESEND: { className: 'activity-status--resend', icon: 'bi-arrow-repeat', label: 'Resend' },
};

function formatCategoryLabel(category) {
    if (!category) {
        return null;
    }

    return category
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function ActivityPreviewSkeleton() {
    return (
        <div className="activity-view-modal__skeleton">
            <div className="activity-view-modal__skeleton-line activity-view-modal__skeleton-line--lg" />
            <div className="activity-view-modal__skeleton-line" />
            <div className="activity-view-modal__skeleton-line activity-view-modal__skeleton-line--md" />
            <div className="activity-view-modal__skeleton-line" />
            <div className="activity-view-modal__skeleton-line activity-view-modal__skeleton-line--sm" />
        </div>
    );
}

function ActivityStatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.WAITING;

    return (
        <span className={`activity-status ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {meta.label || status || 'UNKNOWN'}
        </span>
    );
}

function ActivityMediumBadge({ mediumType }) {
    const meta = MEDIUM_META[mediumType] || {
        icon: 'bi-question-circle',
        className: 'activity-medium--default',
        label: mediumType || 'Unknown',
    };

    return (
        <span className={`activity-medium ${meta.className}`}>
            <i className={`bi ${meta.icon}`}></i>
            {meta.label}
        </span>
    );
}

function MetaItem({ icon, label, value, linkTo }) {
    if (!value) {
        return null;
    }

    return (
        <div className="activity-view-modal__meta-item">
            <span className="activity-view-modal__meta-label">
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
            <span className="activity-view-modal__meta-value">
                {linkTo ? (
                    <Link to={linkTo} className="activity-view-modal__meta-link">
                        {value}
                    </Link>
                ) : (
                    value
                )}
            </span>
        </div>
    );
}

function ActivityPreviewContent({ item }) {
    const content = item?.content;
    const mediumType = item?.mediumType;

    if (!content) {
        return (
            <div className="activity-view-modal__empty">
                <div className="activity-view-modal__empty-icon">
                    <i className="bi bi-file-earmark-text"></i>
                </div>
                <h6 className="fw-normal text-dark mb-1">No preview available</h6>
                <p className="text-muted mb-0 small">
                    This activity does not have stored content to preview.
                </p>
            </div>
        );
    }

    if (mediumType === 'EMAIL' || looksLikeHtml(content)) {
        return (
            <div
                className="activity-view-modal__html-content"
                dangerouslySetInnerHTML={{ __html: content }}
            />
        );
    }

    if (mediumType === 'SMS') {
        return (
            <div className="activity-view-modal__sms-preview">
                <div className="activity-view-modal__sms-bubble">
                    <div className="activity-view-modal__sms-label">
                        <i className="bi bi-chat-left-text me-1"></i>
                        Message
                    </div>
                    <p className="mb-0">{content}</p>
                </div>
            </div>
        );
    }

    if (mediumType === 'CALL') {
        return (
            <div className="activity-view-modal__call-preview">
                <div className="activity-view-modal__call-icon">
                    <i className="bi bi-telephone-outbound"></i>
                </div>
                <h6 className="fw-normal text-dark mb-1">Voice call activity</h6>
                <p className="text-muted mb-0 small text-break">{content}</p>
            </div>
        );
    }

    return (
        <div className="activity-view-modal__text-content">
            {content}
        </div>
    );
}

function looksLikeHtml(content) {
    const trimmed = content.trim();
    return trimmed.startsWith('<') && trimmed.includes('>');
}

function ActivityDetailsModal({ show, onClose, clinicId, activityId, previewItem }) {
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [item, setItem] = useState(previewItem || null);

    useEffect(() => {
        if (!show || !clinicId || !activityId) {
            return undefined;
        }

        let cancelled = false;

        const loadDetails = async () => {
            setLoading(true);
            setLoadFailed(false);
            setItem(previewItem || null);

            try {
                const res = await clinicService.getActivityById(clinicId, activityId);
                if (!cancelled) {
                    setItem(res.data);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load activity details', err);
                    toast.error('Failed to load activity preview');
                    setLoadFailed(true);
                    setItem(previewItem || null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadDetails();

        return () => {
            cancelled = true;
        };
    }, [show, clinicId, activityId, previewItem]);

    const displayItem = useMemo(
        () => ({ ...(previewItem || {}), ...(item || {}) }),
        [previewItem, item]
    );

    const patientLabel = displayItem.patientName
        || [displayItem.firstName, displayItem.lastName].filter(Boolean).join(' ')
        || displayItem.preferredName
        || null;

    const receiverLabel = displayItem.patientId
        ? (patientLabel || displayItem.receiver)
        : displayItem.receiver;
    const patientProfileLink = displayItem.patientId
        ? `/patient-files/${displayItem.patientId}`
        : null;

    const mediumMeta = MEDIUM_META[displayItem.mediumType] || { icon: 'bi-activity' };
    const categoryLabel = formatCategoryLabel(displayItem.category);

    if (!show) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className="activity-view-modal ecx-page"
            contentClassName="activity-view-modal__content border-0 shadow-lg"
        >
            <Modal.Header className="activity-view-modal__header border-0 pb-0">
                <div className="w-100">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-3 min-width-0">
                            <span className="activity-view-modal__icon-badge flex-shrink-0">
                                <i className={`bi ${mediumMeta.icon}`}></i>
                            </span>
                            <div className="min-width-0">
                                <span className="ecx-page-chip mb-2">
                                    <i className="bi bi-eye"></i>
                                    Activity Preview
                                </span>
                                <Modal.Title as="h5" className="fw-normal text-dark mb-2 text-break">
                                    {displayItem.activityName || 'Activity Details'}
                                </Modal.Title>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    {displayItem.status && <ActivityStatusBadge status={displayItem.status} />}
                                    {displayItem.mediumType && (
                                        <ActivityMediumBadge mediumType={displayItem.mediumType} />
                                    )}
                                    {categoryLabel && (
                                        <span className="activity-category-badge">{categoryLabel}</span>
                                    )}
                                    {displayItem.createdAt && (
                                        <span className="activity-view-modal__meta">
                                            <i className="bi bi-calendar3 me-1"></i>
                                            {displayItem.createdAt}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__close-btn flex-shrink-0"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="activity-view-modal__body pt-3">
                <div className="activity-view-modal__summary mb-3">
                    <div className="activity-view-modal__summary-grid">
                        <MetaItem icon="bi-person" label="Patient" value={patientLabel} linkTo={patientProfileLink} />
                        <MetaItem
                            icon="bi-send"
                            label="Receiver"
                            value={receiverLabel}
                            linkTo={displayItem.patientId ? patientProfileLink : null}
                        />
                        <MetaItem icon="bi-clock-history" label="Modified" value={displayItem.modifiedAt} />
                    </div>
                </div>

                <div className="activity-view-modal__preview-panel">
                    {loading ? (
                        <ActivityPreviewSkeleton />
                    ) : loadFailed && !displayItem.content ? (
                        <div className="activity-view-modal__empty">
                            <div className="activity-view-modal__empty-icon">
                                <i className="bi bi-exclamation-circle"></i>
                            </div>
                            <h6 className="fw-normal text-dark mb-1">Preview unavailable</h6>
                            <p className="text-muted mb-0 small">
                                We could not load the full activity preview. Try again later.
                            </p>
                        </div>
                    ) : (
                        <ActivityPreviewContent item={displayItem} />
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="activity-view-modal__footer border-0">
                <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__footer-close-btn"
                    onClick={onClose}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ActivityDetailsModal;
